import { useEffect, useState, useCallback } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { getAdminClient } from "@/lib/supabase-admin";

interface AdminAuthState {
  loading: boolean;
  user: User | null;
  session: Session | null;
  isAdmin: boolean;
}

/**
 * Tracks Supabase auth session for the admin surface and derives whether the
 * signed-in user is in the admin_users allowlist.
 *
 * isAdmin is resolved server-side via the `is_admin()` RPC, not the client's
 * email. Even if a non-allowlisted user receives a magic link by mistake,
 * RLS-protected reads/writes still fail.
 */
export function useAdminAuth(): AdminAuthState & {
  signOut: () => Promise<void>;
} {
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    const supabase = getAdminClient();
    let cancelled = false;

    async function resolveAdmin(currentSession: Session | null) {
      if (!currentSession) {
        if (!cancelled) {
          setIsAdmin(false);
          setLoading(false);
        }
        return;
      }
      const { data, error } = await supabase.rpc("is_admin");
      if (cancelled) return;
      setIsAdmin(!error && data === true);
      setLoading(false);
    }

    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      setSession(data.session);
      void resolveAdmin(data.session);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      setLoading(true);
      void resolveAdmin(newSession);
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const signOut = useCallback(async () => {
    await getAdminClient().auth.signOut();
  }, []);

  return {
    loading,
    user: session?.user ?? null,
    session,
    isAdmin,
    signOut,
  };
}
